import { isEmbed, getPathStationId, getContextStationId } from 'store/websocketMiddleware';

// card-continue-action: a card's built-in Continue action opens the card's own station in a
// new chat with that one card as context. The server mints a snapshot of the card and
// redirects /c/<ref> to the station's chat; `say` prefills the person's opening line, and
// `entry` goes to the station's declared entry_action on arrival (Order starts the order).
// ADR 2026-10-27-card-continue-action.

export const CONTINUE = 'continue';
const MAX_SAY_CHARS = 200;
const ENTRY_RE = /^[A-Za-z0-9._:-]{1,256}$/;

export const isContinueAction = (a) => !!a && typeof a === 'object' && a.builtin === CONTINUE;

export const continueHref = (ref, say, entry) => {
  const q = [say && `say=${encodeURIComponent(say)}`, entry && `entry=${encodeURIComponent(entry)}`]
    .filter(Boolean).join('&');
  return `/c/${encodeURIComponent(ref)}${q ? `?${q}` : ''}`;
};

const cardStation = (structured) => {
  const s = structured && structured.source_station_id;
  return s != null && /^\d+$/.test(String(s)) ? Number(s) : null;
};

// Hidden in the chat of the station the card would continue at: the person is already there.
// A card naming no station was made by the station whose chat shows it.
export function continueShown(structured) {
  if (isEmbed()) return true;
  const here = getPathStationId();
  if (here == null) return true;
  const target = cardStation(structured);
  return target != null && String(target) !== String(here);
}

// The card as its renderer should see it: Continue actions dropped where they're hidden.
export function withoutHiddenContinue(structured) {
  if (!structured || typeof structured !== 'object' || continueShown(structured)) return structured;
  let out = null;
  Object.keys(structured).forEach((k) => {
    if (isContinueAction(structured[k])) {
      out = out || { ...structured };
      delete out[k];
    }
  });
  if (Array.isArray(structured.actions) && structured.actions.some(isContinueAction)) {
    out = out || { ...structured };
    out.actions = structured.actions.filter((a) => !isContinueAction(a));
  }
  return out || structured;
}

// The host's own copy of a Continue that a sandboxed card asks for, or null when the payload the
// host gave the card doesn't carry it. The card's code can't invent a Continue or retarget one.
export function carriedContinue(structured, action) {
  if (!isContinueAction(action) || !structured || typeof structured !== 'object') return null;
  const same = (a) => isContinueAction(a) && a.label === action.label
    && (a.say || null) === (action.say || null) && (a.entry || null) === (action.entry || null);
  const carried = Object.values(structured).concat(Array.isArray(structured.actions) ? structured.actions : []);
  return carried.find(same) || null;
}

const signInHref = () => (isEmbed()
  ? '/login'
  : `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);

// Resolves true once navigation starts, false when the card can't be continued.
export async function continueCard(card, { stationId, say, entry } = {}) {
  // An embed must not navigate itself. Open the tab inside the click, before any
  // await, or the browser blocks it as a popup.
  const tab = isEmbed() ? window.open('', '_blank') : null;
  if (tab) tab.opener = null;
  const go = (href) => {
    if (tab) tab.location.href = href;
    else window.location.assign(href);
    return true;
  };
  try {
    const res = await fetch('/api/continue', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ card, station_id: stationId }),
    });
    if (res.status === 401) return go(signInHref());
    const body = res.ok ? await res.json() : null;
    if (!body || !body.ref) throw new Error('not continuable');
    return go(continueHref(body.ref, say, entry));
  } catch (e) {
    if (tab) tab.close();
    return false;
  }
}

export function runContinueAction(action, structured) {
  const say = typeof action.say === 'string' && action.say.trim()
    ? action.say.trim().slice(0, MAX_SAY_CHARS) : undefined;
  const entry = typeof action.entry === 'string' && ENTRY_RE.test(action.entry)
    ? action.entry : undefined;
  const stationId = getContextStationId();
  return continueCard(structured, { stationId: stationId == null ? undefined : stationId, say, entry });
}
